import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {ProductPageContext} from 'modules/product-card/context';
import {FC, ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {selectProductData} from 'modules/product-card/selectors';
import {useWineshopsForTodayWithStock} from 'modules/wineshops-list/hooks/useWineshopsForTodayWithStock';
import {IWineShop} from 'modules/wineshops-list/models';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {hideModal, showModal} from 'modules/modals/thunks';
import {MODALS} from 'modules/modals/constants';
import {useAddProductInCart} from 'hooks/product/useAddProductInCart';
import {useRouter} from 'next/router';
import {getDefaultReviewsParams} from 'modules/product-card/helpers';
import {useGetReviewsQuery, useGetReviewsTotalQuery} from 'modules/product-card/services';
import {getDefaultReviewsTotalParams} from 'modules/products/helpers';
import {useAddExpressOrderMutation, useGetExpressOrderQuery} from 'modules/products/services';
import {IExpressOrder} from 'plugins/modules/filter/types';
import {invalidateProduct} from 'modules/product-card/utils/invalidateProduct';
import {ISeo} from 'modules/seo/types';

interface IProps {
    children: ReactNode;
    seo?: ISeo | null;
}

const useProductReviews = (productCode: string) => {
    const defaultParams = useMemo(() => getDefaultReviewsParams(productCode), [productCode]);
    const defaultTotalParams = useMemo(() => getDefaultReviewsTotalParams(productCode), [productCode]);

    const {data: reviews} = useGetReviewsQuery(defaultParams);
    const {data: reviewsTotal} = useGetReviewsTotalQuery(defaultTotalParams);

    return {reviews, reviewsTotal};
};

const useWineshop = (productCode?: string) => {
    const {data: expressOrderData, isFetching: isWineShopLoading} = useGetExpressOrderQuery();
    const [addExpressOrder] = useAddExpressOrderMutation();

    const dispatch = useAppDispatch();
    const addProductInCart = useAddProductInCart();

    const [wineShop, setWineShop] = useState<IExpressOrder>();
    const [wineshopForReserveProduct, setWineshopForReserveProduct] = useState<IWineShop>();
    const [isAddToCartWhenReserving, setIsAddToCartWhenReserving] = useState<boolean>(false);

    const onReserveProduct = useCallback(
        (wineshop: IWineShop) => {
            setWineshopForReserveProduct(wineshop);
            dispatch(showModal(MODALS.PRODUCT_RESERVE.name));
            dispatch(hideModal(MODALS.PRODUCT_PICKUP.name));
        },
        [dispatch],
    );

    const onSelectShop = useCallback(
        async (result: IWineShop) => {
            try {
                const response = await addExpressOrder({
                    idVinoteki: Number(result.id),
                });

                if ('error' in response) {
                    return;
                }

                const {ok} = response.data;
                if (!ok) {
                    return;
                }

                await addProductInCart();
                setWineShop(result as unknown as IExpressOrder);

                if (productCode) {
                    await dispatch(invalidateProduct(productCode)).finally(() =>
                        dispatch(hideModal(MODALS.PRODUCT_PICKUP.name)),
                    );
                } else {
                    dispatch(hideModal(MODALS.PRODUCT_PICKUP.name));
                }
            } catch (e) {
                console.error(e);
            }
        },
        [addExpressOrder, addProductInCart, dispatch, productCode],
    );

    useEffect(() => {
        let shop = expressOrderData?.data;
        if (Array.isArray(shop)) {
            shop = undefined;
        }
        setWineShop(shop);
    }, [expressOrderData]);

    return {
        wineShop,
        wineshopForReserveProduct,
        setWineshopForReserveProduct,
        onReserveProduct,
        onSelectShop,
        isWineShopLoading,
        isAddToCartWhenReserving,
        setIsAddToCartWhenReserving,
    };
};

export const ProductPageProvider: FC<IProps> = ({children, seo}) => {
    const {query} = useRouter();
    const productData = useAppSelector(selectProductData);
    const {canBuy, canReserve} = productData || {};
    const wineshopsForTodayWithStock = useWineshopsForTodayWithStock(productData);

    const routerProductCode = Array.isArray(query.code) ? query.code[0] : query.code;
    const productCode = productData?.code ?? routerProductCode ?? '';

    const {reviews, reviewsTotal} = useProductReviews(query.code as string);
    const {
        wineShop,
        wineshopForReserveProduct,
        setWineshopForReserveProduct,
        onReserveProduct,
        onSelectShop,
        isWineShopLoading,
        isAddToCartWhenReserving,
        setIsAddToCartWhenReserving,
    } = useWineshop(productCode);

    return (
        <ProductPageContext.Provider
            value={{
                canBuy,
                canReserve,
                onReserveProduct,
                onSelectShop,
                reviews,
                seo,
                reviewsTotal,
                setWineshopForReserveProduct,
                wineshopForReserveProduct,
                wineshopsForTodayWithStock,
                expressWineshop: wineShop,
                isExpressWineshopLoading: isWineShopLoading,
                isAddToCartWhenReserving,
                setIsAddToCartWhenReserving,
            }}
        >
            {children}
        </ProductPageContext.Provider>
    );
};
